<script setup lang="ts">
import type { CustomerServiceProps } from './CustomerService.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import { iconMapping } from './CustomerService.iconMapping'
import type { UILinkProps } from '@design-system/components/UI/Link/UILink.props'

const props = defineProps<CustomerServiceProps>()

const { openDialog } = useDialog()
const { cmsContentCustomerService, checkoutPickupStoreSidebar } = SIDEBAR_IDS
const { cmsContentId } = useHelpArea()

const isChildComponent = props?.isChildComponent ?? false

const text = computed(() => props?.content?.text)

const links = computed(() => props?.content?.links ?? [])

const contents = computed(() => props?.content?.body ?? [])

const isPreface = <ContentItem extends { id?: string }>(
  contentItem: ContentItem
) => !contentItem.id

const filteredContent = computed(() => {
  const data = contents.value.filter(
    (contentItem: any) => !isPreface(contentItem)
  )
  const structuredContent = links.value.length
    ? links.value.map(link => {
        const contentItem = data.find(
          (contentItem: any) => contentItem.id === link.target.target
        )
        return {
          id: link.target.target,
          label: link.target.label,
          text: contentItem?.text,
          accordrionSection: contentItem?.accordrionSection,
          cta: contentItem?.cta,
        }
      })
    : contents.value.map((contentItem: any) => ({
        id: contentItem.id,
        label: contentItem.label,
        text: contentItem.text,
        accordrionSection: contentItem.accordrionSection,
        cta: contentItem.cta,
      }))

  return structuredContent
})

const handleClick = (id: string) => {
  if (id === checkoutPickupStoreSidebar)
    return openDialog(checkoutPickupStoreSidebar)
  cmsContentId.value = id
  openDialog(cmsContentCustomerService)
}
</script>

<template>
  <div
    :class="[
      isChildComponent
        ? 'bg-neutral-white text-neutral-black'
        : 'mt-lg md:mt-4xl mb-md md:mb-xxl gap-md md:gap-xxl flex flex-col',
    ]"
  >
    <div
      class="gap-sm flex flex-col"
      :class="{ 'mb-xs md:mb-sm': isChildComponent }"
    >
      <slot name="text">
        <UtilsMarkdown
          v-if="text"
          class="text-book-3 text-neutral-black"
          :content="text"
          container="h2"
        />
      </slot>
      <div v-if="links.length" class="gap-xs flex flex-wrap">
        <UILink
          v-for="link in links"
          :key="`link-${link?.target?.label}`"
          class="px-sm py-xs bg-neutral-white text-neutral-black underline-effect-default"
          v-bind="link.target as UILinkProps"
        >
          <UtilsMarkdown
            v-if="link?.target?.label"
            :content="link?.target?.label"
            class="text-link-6 text-effect"
          />
        </UILink>
      </div>
    </div>
    <div
      class="bg-neutral-white text-neutral-black"
      :class="[isChildComponent ? 'p-0' : 'p-md md:p-xxl']"
    >
      <div class="gap-md md:gap-xxl flex flex-col">
        <div
          v-for="content in filteredContent"
          :key="content.id"
          class="gap-md flex flex-col"
        >
          <div class="md:gap-md flex flex-col gap-3">
            <UtilsMarkdown
              v-if="content?.label"
              :id="content?.id"
              :content="content?.label"
              class="text-medium-4"
              container="h3"
            />
            <UtilsMarkdown
              v-if="content?.text"
              :id="content?.id"
              :content="content.text"
              class="page-structure"
            />
          </div>
          <div
            v-if="content?.accordrionSection"
            class="gap-xs flex w-full flex-col"
          >
            <span
              v-if="content?.accordrionSection?.name"
              class="text-book-5 uppercase"
            >
              {{ content?.accordrionSection?.name }}
            </span>
            <div
              v-if="!!content?.accordrionSection?.listOfContentItems?.length"
            >
              <UIDropdown
                v-for="accordion in content?.accordrionSection
                  ?.listOfContentItems"
                :key="`faq-accordion-${accordion?.title}`"
                :title="accordion?.title! ?? ''"
                title-wrapper="h4"
                is-help-area
                class="[&_h4]:text-book-6 border-b [&_h4]:text-left"
              >
                <UtilsMarkdown
                  v-if="accordion?.body"
                  class="text-book-6 first-letter:uppercase"
                  :content="accordion?.body"
                />
              </UIDropdown>
            </div>
          </div>
          <AtomsCta
            v-if="content?.cta"
            anatomy="secondary"
            aria-haspopup="dialog"
            @click-handler="handleClick(content?.cta?.content!)"
          >
            <template #label>
              <div class="gap-xs flex items-center">
                <component
                  :is="
                    iconMapping[content?.cta?.icon as keyof typeof iconMapping]
                  "
                  v-if="content?.cta?.icon && content?.cta?.icon in iconMapping"
                />
                {{ content?.cta?.title }}
              </div>
            </template>
          </AtomsCta>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-structure {
  :deep(h3) {
    @apply text-medium-4 md:mb-md mb-3;
  }

  :deep(table) {
    @apply gap-x-sm gap-y-md my-5 flex w-full flex-col overflow-scroll;

    ul {
      @apply pt-0;
    }
  }

  :deep(tbody) {
    @apply gap-y-md flex flex-wrap;
  }

  :deep(th) {
    @apply text-medium-5 min-w-[231px] text-left;
  }

  :deep(tr) {
    @apply gap-x-sm flex;
  }

  :deep(td) {
    @apply min-w-[231px];
  }

  :deep(td),
  :deep(p) {
    @apply text-book-6;
  }

  :deep(a) {
    @apply underline;
  }

  :deep(ul) {
    @apply list-disc pl-5;
  }

  :deep(ol) {
    @apply text-book-6 list-decimal pl-5;
  }
}
</style>
